import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Lock, Inbox, Mail, ArrowLeft, RefreshCw, Loader2, Check, Dot, Reply, Send, X } from "lucide-react";
import { ADMIN } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [updating, setUpdating] = useState(null);
  const [replyingId, setReplyingId] = useState(null);
  const [replyBody, setReplyBody] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [authError, setAuthError] = useState("");

  const load = async (t) => {
    setLoading(true);
    setAuthError("");
    try {
      const res = await axios.get(`${API}/contact`, {
        headers: { "x-admin-token": t },
      });
      setMessages(res.data);
      setAuthed(true);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        const msg = "Too many attempts. Please try again in a few minutes.";
        setAuthError(msg);
        toast.error(msg);
      } else if (status === 401) {
        const msg = "Invalid access token.";
        setAuthError(msg);
        toast.error(msg);
      } else {
        const msg = "Could not load messages. Please try again.";
        setAuthError(msg);
        toast.error(msg);
      }
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!token) return toast.error("Enter your access token.");
    load(token);
  };

  const toggleRead = async (m) => {
    setUpdating(m.id);
    try {
      const res = await axios.patch(
        `${API}/contact/${m.id}`,
        { read: !m.read },
        { headers: { "x-admin-token": token } }
      );
      setMessages((prev) => prev.map((x) => (x.id === m.id ? res.data : x)));
      toast.success(res.data.read ? "Marked as read." : "Marked as unread.");
    } catch (err) {
      toast.error("Could not update status.");
    } finally {
      setUpdating(null);
    }
  };

  const openReply = (m) => {
    setReplyingId(m.id);
    setReplyBody("");
  };

  const closeReply = () => {
    setReplyingId(null);
    setReplyBody("");
  };

  const sendReply = async (m) => {
    if (!replyBody.trim()) return toast.error("Write a reply first.");
    setSendingReply(true);
    try {
      const res = await axios.post(
        `${API}/contact/${m.id}/reply`,
        { body: replyBody },
        { headers: { "x-admin-token": token } }
      );
      setMessages((prev) => prev.map((x) => (x.id === m.id ? res.data : x)));
      toast.success(`Reply sent to ${m.email}.`);
      closeReply();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail ? `Could not send: ${detail}` : "Could not send reply.");
    } finally {
      setSendingReply(false);
    }
  };

  const fmt = (iso) =>
    new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });

  const unreadCount = messages.filter((m) => !m.read).length;
  const matchesFilter = (m) => {
    if (filter === "unread") return !m.read;
    if (filter === "read") return m.read;
    return true;
  };
  const visible = messages.filter(matchesFilter);

  const FilterBtn = ({ value, label }) => (
    <button
      data-testid={`${ADMIN.filter}-${value}`}
      onClick={() => setFilter(value)}
      className={`font-mono-plex text-[11px] uppercase tracking-[0.12em] px-4 py-2 rounded-full border transition-colors duration-300 ${
        filter === value
          ? "bg-[#16324f] text-[#f5f6f2] border-[#16324f]"
          : "border-[#c2c6bd] text-[#48606f] hover:border-[#16324f]"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#eef0e9] relative">
      <div className="grain" />
      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 py-14">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-mono-plex text-[12px] uppercase tracking-[0.14em] text-[#48606f] hover:text-[#16324f] mb-10"
        >
          <ArrowLeft size={14} /> Back to site
        </Link>

        {!authed ? (
          <div className="max-w-md">
            <Lock size={28} strokeWidth={1.4} className="text-[#b5423a] mb-6" />
            <h1 className="font-serif-display text-4xl md:text-5xl text-[#16324f] leading-none">
              Message inbox
            </h1>
            <p className="mt-4 text-[#48606f]">Enter the access token to view enquiries submitted through the portfolio.</p>
            <form onSubmit={submit} className="mt-8 space-y-6" data-testid={ADMIN.form}>
              <div>
                <label className="font-mono-plex text-[11px] uppercase tracking-[0.16em] text-[#48606f]">Access token</label>
                <input
                  type="password"
                  data-testid={ADMIN.token}
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  className="w-full mt-2 bg-transparent border-b border-[#c2c6bd] focus:border-[#b5423a] outline-none py-3 text-[#16324f] text-lg font-serif-display transition-colors duration-300"
                />
              </div>
              <button
                data-testid={ADMIN.submit}
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 bg-[#16324f] text-[#f5f6f2] font-mono-plex text-[12.5px] uppercase tracking-[0.12em] px-7 py-3.5 rounded-full hover:bg-[#b5423a] transition-colors duration-400 disabled:opacity-60"
              >
                {loading ? <Loader2 size={15} className="animate-spin" /> : <Inbox size={15} />}
                Unlock inbox
              </button>
              {authError && (
                <p data-testid="admin-auth-error" className="text-[#b5423a] font-mono-plex text-[12.5px]">
                  {authError}
                </p>
              )}
            </form>
          </div>
        ) : (
          <div data-testid={ADMIN.list}>
            <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
              <div>
                <span className="eyebrow">
                  {messages.length} message{messages.length !== 1 ? "s" : ""}
                  {unreadCount > 0 ? ` · ${unreadCount} unread` : ""}
                </span>
                <h1 className="font-serif-display text-4xl md:text-5xl text-[#16324f] leading-none mt-3">
                  Inbox
                </h1>
              </div>
              <button
                onClick={() => load(token)}
                className="flex items-center gap-2 border border-[#16324f] text-[#16324f] font-mono-plex text-[12px] uppercase tracking-[0.12em] px-5 py-2.5 rounded-full hover:bg-[#16324f] hover:text-[#f5f6f2] transition-colors duration-400"
              >
                <RefreshCw size={13} /> Refresh
              </button>
            </div>

            <div className="flex items-center gap-2.5 mb-8">
              <FilterBtn value="all" label={`All (${messages.length})`} />
              <FilterBtn value="unread" label={`Unread (${unreadCount})`} />
              <FilterBtn value="read" label={`Read (${messages.length - unreadCount})`} />
            </div>

            {visible.length === 0 ? (
              <p className="text-[#48606f]">
                {messages.length === 0 ? "No messages yet." : "No messages in this view."}
              </p>
            ) : (
              <div className="space-y-4">
                {visible.map((m) => (
                  <div
                    key={m.id}
                    className={`border transition-colors duration-400 p-6 rounded-lg ${
                      m.read
                        ? "bg-[#eef0e9] border-[#d2d5ce]"
                        : "bg-[#f5f6f2] border-[#16324f] shadow-[0_1px_0_#16324f]"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2.5">
                          {!m.read && (
                            <span className="inline-flex items-center gap-1 font-mono-plex text-[10px] uppercase tracking-[0.14em] text-[#b5423a] bg-[#b5423a]/10 px-2 py-0.5 rounded-full">
                              <Dot size={14} className="-mx-1" /> New
                            </span>
                          )}
                          {m.replied && (
                            <span className="inline-flex items-center gap-1 font-mono-plex text-[10px] uppercase tracking-[0.14em] text-[#2e7d6b] bg-[#2e7d6b]/10 px-2 py-0.5 rounded-full">
                              <Check size={11} /> Replied
                            </span>
                          )}
                          <h3 className="font-serif-display text-xl text-[#16324f]">{m.name}</h3>
                        </div>
                        <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1.5 mt-1 font-mono-plex text-[12px] text-[#2e7d6b] link-underline w-fit">
                          <Mail size={12} /> {m.email}
                        </a>
                      </div>
                      <div className="flex flex-col items-end gap-2.5">
                        <span className="font-mono-plex text-[11px] text-[#48606f]">{fmt(m.created_at)}</span>
                        <div className="flex items-center gap-2">
                          <button
                            data-testid={`${ADMIN.replyBtn}-${m.id}`}
                            onClick={() => (replyingId === m.id ? closeReply() : openReply(m))}
                            className="inline-flex items-center gap-1.5 font-mono-plex text-[10.5px] uppercase tracking-[0.12em] px-3.5 py-1.5 rounded-full border border-[#b5423a] text-[#b5423a] hover:bg-[#b5423a] hover:text-[#f5f6f2] transition-colors duration-300"
                          >
                            <Reply size={12} /> Reply
                          </button>
                          <button
                            data-testid={`${ADMIN.readToggle}-${m.id}`}
                            onClick={() => toggleRead(m)}
                            disabled={updating === m.id}
                            className={`inline-flex items-center gap-1.5 font-mono-plex text-[10.5px] uppercase tracking-[0.12em] px-3.5 py-1.5 rounded-full border transition-colors duration-300 disabled:opacity-50 ${
                              m.read
                                ? "border-[#c2c6bd] text-[#48606f] hover:border-[#16324f]"
                                : "border-[#16324f] bg-[#16324f] text-[#f5f6f2] hover:bg-[#b5423a] hover:border-[#b5423a]"
                            }`}
                          >
                            {updating === m.id ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <Check size={12} />
                            )}
                            {m.read ? "Mark unread" : "Mark read"}
                          </button>
                        </div>
                      </div>
                    </div>
                    {m.subject && (
                      <p className="mt-4 font-mono-plex text-[11px] uppercase tracking-[0.14em] text-[#b5423a]">{m.subject}</p>
                    )}
                    <p className="mt-2 text-[15px] text-[#16324f] leading-relaxed whitespace-pre-wrap">{m.message}</p>

                    {replyingId === m.id && (
                      <div className="mt-5 pt-5 border-t border-[#d2d5ce]">
                        <div className="flex items-center justify-between mb-2">
                          <label className="font-mono-plex text-[11px] uppercase tracking-[0.14em] text-[#48606f]">
                            Reply to {m.name}
                          </label>
                          <button onClick={closeReply} className="text-[#8b96a1] hover:text-[#16324f] transition-colors">
                            <X size={16} />
                          </button>
                        </div>
                        <textarea
                          data-testid={`${ADMIN.replyInput}-${m.id}`}
                          value={replyBody}
                          onChange={(e) => setReplyBody(e.target.value)}
                          rows={4}
                          placeholder={`Write your reply — it will be emailed to ${m.email}.`}
                          className="w-full bg-white border border-[#d2d5ce] rounded-xl px-4 py-3 text-[#16324f] text-[15px] placeholder:text-[#8b96a1] outline-none focus:border-[#b5423a] focus:ring-2 focus:ring-[#b5423a]/15 resize-none transition-[border-color,box-shadow] duration-300"
                        />
                        <button
                          data-testid={`${ADMIN.replySend}-${m.id}`}
                          onClick={() => sendReply(m)}
                          disabled={sendingReply}
                          className="mt-3 inline-flex items-center gap-2 bg-[#16324f] text-[#f5f6f2] font-mono-plex text-[11.5px] uppercase tracking-[0.12em] px-6 py-3 rounded-full hover:bg-[#b5423a] transition-colors duration-400 disabled:opacity-60"
                        >
                          {sendingReply ? <Loader2 size={14} className="animate-spin" /> : <Send size={13} />}
                          {sendingReply ? "Sending…" : "Send reply"}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
